// Where the link in our "Confirm your email" email lands
// (functions/lib/accountEmail.js): buildbotsnow.com/account/confirm?mode=…&oobCode=….
//
// It hands the one-time code back to Firebase, which confirms the address, or
// for a corrected address makes it the account's (mode verifyAndChangeEmail).
// Opened signed in or not, in this browser or another: the code is all it
// needs. Signed in here, the dashboard is told at once, so a bot that sent
// handoffs nowhere gets the address (POST /me/email-confirmed); in another
// tab, the banner there notices by itself.

import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ActionCodeOperation, applyActionCode, checkActionCode } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import * as api from "../lib/api";
import { initFirebase } from "../lib/firebase";
import { PRODUCT_NAME } from "../lib/brand";
import { ColorModeToggle } from "../components/ColorModeToggle";
import Footer from "../components/Footer";
import { Logo } from "../components/Logo";
import { Card, LoadingPage } from "../components/ui";

type State =
  | { kind: "working" }
  | { kind: "confirmed"; email: string }
  | { kind: "changed"; email: string }
  | { kind: "failed"; message: string };

const LINK_FAILURES: Record<string, string> = {
  "auth/expired-action-code": "This link has expired. Send a new one from your dashboard.",
  "auth/invalid-action-code": "This link was already used, or is not complete. If your email is not confirmed yet, send a new link from your dashboard.",
  "auth/email-already-in-use": "That address already has a Build Bots Now account, so it cannot be moved to this one."
};

export default function AccountConfirm() {
  const [params] = useSearchParams();
  const { user, signOut } = useAuth();
  const [state, setState] = useState<State>({ kind: "working" });
  // React runs effects twice in development; a code works only once.
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const code = params.get("oobCode");
    if (!code) {
      setState({ kind: "failed", message: "This link is not complete. Open it from the email again." });
      return;
    }
    (async () => {
      try {
        const auth = await initFirebase();
        const info = await checkActionCode(auth, code);
        await applyActionCode(auth, code);
        const changing = info.operation === ActionCodeOperation.VERIFY_AND_CHANGE_EMAIL;
        const email = info.data.email || "";
        if (changing) {
          setState({ kind: "changed", email });
          return;
        }
        // Signed in here: refresh the session so the server sees the
        // confirmation, and connect handoffs that went nowhere.
        if (auth.currentUser) {
          await auth.currentUser.reload().catch(() => undefined);
          await auth.currentUser.getIdToken(true).catch(() => undefined);
          await api.emailConfirmed().catch(() => undefined);
        }
        setState({ kind: "confirmed", email });
      } catch (err) {
        const code = (err as { code?: string }).code || "";
        setState({ kind: "failed", message: LINK_FAILURES[code] || "That did not work. Try the link again, or send a new one from your dashboard." });
      }
    })();
  }, [params]);

  return (
    <div className="flex min-h-screen flex-col bg-slate-50">
      <header className="border-b border-slate-200 bg-surface">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3">
          <Link to="/" aria-label={`${PRODUCT_NAME} home`} className="flex">
            <Logo />
          </Link>
          <ColorModeToggle />
        </div>
      </header>
      <main className="mx-auto w-full max-w-lg flex-1 px-4 py-12">
        {state.kind === "working" ? (
          <LoadingPage label="Confirming your email…" />
        ) : (
          <Card>
            {state.kind === "confirmed" && (
              <>
                <h1 className="text-xl font-semibold text-slate-900">Email confirmed</h1>
                <p className="mt-2 text-slate-600">
                  <strong>{state.email}</strong> is confirmed. Password resets, receipts and handoff alerts will reach you there.
                </p>
                <Link to={user ? "/agents" : "/signin"} className="mt-6 inline-block rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700">
                  {user ? "Go to your agents" : "Sign in"}
                </Link>
              </>
            )}
            {state.kind === "changed" && (
              <>
                <h1 className="text-xl font-semibold text-slate-900">Your email is changed</h1>
                <p className="mt-2 text-slate-600">
                  Your account&apos;s email is now <strong>{state.email}</strong>. Sign in with it from now on.
                </p>
                <button
                  type="button"
                  onClick={() => void (user ? signOut() : Promise.resolve()).then(() => window.location.assign("/signin"))}
                  className="mt-6 inline-block rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
                >
                  Sign in with {state.email}
                </button>
              </>
            )}
            {state.kind === "failed" && (
              <>
                <h1 className="text-xl font-semibold text-slate-900">This link did not work</h1>
                <p className="mt-2 text-slate-600">{state.message}</p>
                <Link to="/agents" className="mt-6 inline-block text-sm font-medium text-brand-700 underline">
                  Go to your dashboard
                </Link>
              </>
            )}
          </Card>
        )}
      </main>
      <Footer />
    </div>
  );
}
